/**
 * Krok NAB1 — RUCNI ODSKRTAVANI BODU naboru (stav obrazovky).
 *
 * Nalez R.B. 7. 10. 2026: kroucky v „Ještě nevíme" vypadaly jako zaskrtavatka,
 * ale nereagovaly — body se plnily jen z rozboru nahravky. Makler si ted bod
 * odskrtne sam a u bodu s hodnotou ji hned zapise.
 *
 * PROC VLASTNI ULOZISTE. Odskrtnuti musi prezit obnoveni stranky i BEZ SITE —
 * tentyz duvod, proc ma nabor offline frontu (na schuzce u majitele signal
 * casto neni), jenze fronta odeslanou vec zahodi, takze stav obrazovky z ni
 * precist nejde. `localStorage` smi v `src/**` jen `stores/session.ts` (krok
 * S3) a je urceny pro PREDVOLBY prohlizece, ne pro rozpracovany nabor;
 * IndexedDB je uz pro nabor zavedena (`field-intake-queue.ts`).
 *
 * PROC VLASTNI DATABAZE, a ne druhy sklad ve `reos-field-intake`: pridani
 * skladu je zmena verze databaze, kterou IndexedDB provede jen po ZAVRENI
 * vsech otevrenych spojeni. Fronta v te chvili drzi spojeni s nahravkami,
 * takze by se upgrade zablokoval (`onblocked`) a nabor by prestal odesilat.
 * Dve male databaze jsou tady levnejsi nez to riziko.
 *
 * Hodnota odskrtnuteho bodu jde NAVIC do relace naboru (fronta -> `manualFact`
 * -> navrh rozboru s puvodem „ručně"), takze se do zakazky propise stejnou
 * cestou jako udaj z nahravky. Tohle uloziste je jen stav OBRAZOVKY.
 */

/** Odkud vime, ze bod je zjisteny. `manual` ma vzdy prednost (rozbor nesmi prepsat). */
export type ChecklistSource = 'manual' | 'recording';

export interface ChecklistMark {
  readonly factKey: string;
  readonly source: ChecklistSource;
  /** `undefined` = „zjisteno, hodnotu nezapisujeme" (zustava v poznamce nebo v nahravce). */
  readonly value?: unknown;
}

/** Stav checklistu: klic pole -> znacka. Bez znacky = nezjisteno. */
export type ChecklistState = Readonly<Record<string, ChecklistMark>>;

export interface ChecklistStore {
  load(scope: string): Promise<ChecklistState>;
  save(scope: string, state: ChecklistState): Promise<void>;
  clear(scope: string): Promise<void>;
  /**
   * Krok KLIKFIX1 (vada V8) — ROZPRACOVANY NABOR PREZIJE OBNOVENI.
   *
   * Klikaci test 29. 10.: makler pridal 3 fotky, poznamku, dispozici a plochu,
   * obnovil stranku a dostal UVODNI obrazovku; „Diktovat si sám" zalozilo
   * NOVOU prazdnou relaci („0 fotek", „0 údajů"), prestoze stranka slibuje
   * „Co se nestihne odeslat, zůstane v zařízení". Fotky a udaje ve fronte
   * pritom lezely dal, jen k nim obrazovka nemela cestu: id relace zilo
   * pouze v pameti stranky.
   *
   * Id relace jde do TEHOZ zapisu jako odskrtnute body (jeden radek na
   * nemovitost) — zadna druha databaze ani druhy sklad; duvody v hlavicce
   * tohoto souboru plati bez zmeny.
   */
  loadSession(scope: string): Promise<string | null>;
  saveSession(scope: string, sessionId: string | null): Promise<void>;
}

/**
 * Pod jakym klicem stav zije. NE podle relace naboru: po obnoveni stranky
 * zacina nova relace (`/nabor` nema navrat do rozdelane relace), ale nemovitost
 * je tataz — zakazka, nebo adresa z formulare „Nová".
 */
export function checklistScope(caseId: string | null, address: string): string {
  if (caseId !== null) return `case:${caseId}`;
  const addr = address.trim().toLowerCase();
  return addr === '' ? 'bez-zakazky' : `addr:${addr}`;
}

export class MemoryChecklistStore implements ChecklistStore {
  private readonly scopes = new Map<string, ChecklistState>();
  private readonly sessions = new Map<string, string>();
  async load(scope: string): Promise<ChecklistState> {
    return this.scopes.get(scope) ?? {};
  }
  async save(scope: string, state: ChecklistState): Promise<void> {
    this.scopes.set(scope, state);
  }
  async clear(scope: string): Promise<void> {
    this.scopes.delete(scope);
    this.sessions.delete(scope);
  }
  async loadSession(scope: string): Promise<string | null> {
    return this.sessions.get(scope) ?? null;
  }
  async saveSession(scope: string, sessionId: string | null): Promise<void> {
    if (sessionId === null) this.sessions.delete(scope);
    else this.sessions.set(scope, sessionId);
  }
}

const DB_NAME = 'reos-field-intake-checklist';
const STORE = 'scopes';

/**
 * Radek ulozeny pod nemovitosti. `session` je id ROZPRACOVANE relace naboru
 * (krok KLIKFIX1, vada V8); `null`/chybejici = zadny nabor nebezi. Pole se
 * pridava BEZ zmeny verze databaze — IndexedDB sklad schema nevynucuje,
 * takze starsi radek bez `session` se precte jako „zadna relace".
 */
interface ChecklistRow {
  readonly scope: string;
  readonly marks: ChecklistState;
  readonly session?: string | null | undefined;
}

/** Jeden zapis na nemovitost (`{ scope, marks }`) — cteni i zapis je jedna operace. */
export class IndexedDbChecklistStore implements ChecklistStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1);
      open.onupgradeneeded = () => {
        open.result.createObjectStore(STORE, { keyPath: 'scope' });
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error ?? new Error('indexeddb'));
    });
    return this.dbPromise;
  }

  private async tx<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('indexeddb'));
    });
  }

  /** Cely radek nemovitosti — odskrtnute body a rozpracovana relace naboru. */
  private async row(scope: string): Promise<ChecklistRow | undefined> {
    return this.tx('readonly', (s) => s.get(scope) as IDBRequest<ChecklistRow | undefined>);
  }

  async load(scope: string): Promise<ChecklistState> {
    try {
      return (await this.row(scope))?.marks ?? {};
    } catch {
      // Uloziste nedostupne (privatni rezim) — nabor funguje dal, jen se stav nepamatuje.
      return {};
    }
  }

  async save(scope: string, state: ChecklistState): Promise<void> {
    try {
      // Id relace se ZACHOVA: `put` prepisuje cely radek, takze bez precteni
      // puvodniho by kazde odskrtnuti bodu zahodilo rozpracovany nabor.
      const session = (await this.row(scope))?.session ?? null;
      await this.tx('readwrite', (s) => s.put({ scope, marks: state, session }));
    } catch {
      // viz load
    }
  }

  async loadSession(scope: string): Promise<string | null> {
    try {
      return (await this.row(scope))?.session ?? null;
    } catch {
      return null;
    }
  }

  async saveSession(scope: string, sessionId: string | null): Promise<void> {
    try {
      const marks = (await this.row(scope))?.marks ?? {};
      await this.tx('readwrite', (s) => s.put({ scope, marks, session: sessionId }));
    } catch {
      // viz load
    }
  }

  async clear(scope: string): Promise<void> {
    try {
      await this.tx('readwrite', (s) => s.delete(scope));
    } catch {
      // viz load
    }
  }
}

let shared: ChecklistStore | null = null;

/** Jedno uloziste na prohlizec (stejny vzor jako `intakeQueue()`). */
export function intakeChecklistStore(): ChecklistStore {
  shared ??= typeof indexedDB === 'undefined' ? new MemoryChecklistStore() : new IndexedDbChecklistStore();
  return shared;
}
